import { useState } from 'react'
import {
  APP_FEATURE_DEFAULTS,
  APP_FEATURE_DESCRIPTIONS,
  APP_FEATURE_FLAG_KEYS,
  APP_FEATURE_LABELS,
  APP_FEATURE_ORDER,
  AppFeature,
  FeatureListItemDto,
} from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { usePermissions } from '../../contexts/PermissionsContext'
import ToggleInput from '../ui/ToggleInput'

interface AppFeatureTogglesCardProps {
  flags: FeatureListItemDto[]
  onUpdated: (updated: FeatureListItemDto) => void
}

/**
 * Kill switches for features under test. Each switch applies to all users at once: the app
 * reacts over the socket and the backend stops serving the feature, so turning one off is instant.
 */
const AppFeatureTogglesCard = ({ flags, onUpdated }: AppFeatureTogglesCardProps) => {
  const { setFeatureEnabled } = usePermissions()
  const [saving, setSaving] = useState<AppFeature | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isEnabled = (feature: AppFeature): boolean => {
    const value = flags.find(f => f.key === APP_FEATURE_FLAG_KEYS[feature])?.payload?.value
    return typeof value === 'boolean' ? value : APP_FEATURE_DEFAULTS[feature]
  }

  const toggle = async (feature: AppFeature) => {
    const next = !isEnabled(feature)
    // Turning off must stay one click; turning on goes live for every user, so confirm it.
    if (next && !window.confirm(`Turn on ${APP_FEATURE_LABELS[feature]} for all users?`)) {
      return
    }
    setSaving(feature)
    setError(null)
    try {
      onUpdated(await adminService.setFeatureValue(APP_FEATURE_FLAG_KEYS[feature], next))
      setFeatureEnabled(feature, next)
    } catch (err: any) {
      setError(err?.friendlyMessage || err?.message || `Could not update ${APP_FEATURE_LABELS[feature]}.`)
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="card">
      <div className="feature-flag-title">Features</div>
      <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
        On: live for all users. Off: hidden in the app and refused by the API right away.
      </div>
      {error && <div className="error-message">{error}</div>}
      {APP_FEATURE_ORDER.map(feature => (
        <div key={feature} className="feature-flag-row app-feature-toggle-row">
          <div>
            <div className="feature-flag-title">{APP_FEATURE_LABELS[feature]}</div>
            <div className="feature-flag-subtitle">{APP_FEATURE_DESCRIPTIONS[feature]}</div>
          </div>
          <div className="feature-flag-actions">
            {saving === feature && <span className="feature-flag-subtitle">Saving…</span>}
            <ToggleInput
              value={isEnabled(feature)}
              aria-label={APP_FEATURE_LABELS[feature]}
              onChange={() => {
                if (saving === null) toggle(feature)
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default AppFeatureTogglesCard
